import type { Money } from '@micarahr/shared-types';

/**
 * Money helpers. Amounts are always integers in minor units (centavos) so no
 * payroll figure is ever produced by floating-point arithmetic.
 */

export const ZERO_PHP: Money = { minorUnits: 0, currency: 'PHP' };

export function money(minorUnits: number): Money {
  return { minorUnits: Math.round(minorUnits), currency: 'PHP' };
}

export function fromPesos(amount: number): Money {
  return { minorUnits: Math.round(amount * 100), currency: 'PHP' };
}

export function toPesos(value: Money): number {
  return value.minorUnits / 100;
}

/**
 * A decimal string from the API, as centavos.
 *
 * The API stores money as `Decimal(18,4)` and serialises it as a string —
 * `"1234.5"`, `"1234.50"` and `"1234.5000"` are one amount spelled three ways,
 * because Prisma drops trailing zeros. The frontend's `Money` is integer
 * centavos. Something has to convert, and `Number(value) * 100` is not it:
 * that is the float arithmetic every other rule here exists to avoid, and it
 * turns `"11.35"` into `1134.9999999999998`.
 *
 * So this works on the digits. Split at the point, take two fraction digits as
 * centavos, and round half-away-from-zero on the third — which is what a peso
 * figure printed from a four-decimal store must do somewhere, done once here
 * rather than differently in each screen.
 *
 * Returns null for absent or unparseable input rather than zero. A missing
 * total and a total of nothing are different facts, and a payroll screen that
 * renders ₱0.00 for "the server did not send this" states something false —
 * which is worse than the `₱NaN` that made this function necessary, because
 * ₱NaN at least announces itself.
 */
export function moneyFromDecimalString(value: string | null | undefined): Money | null {
  if (value === null || value === undefined || value === '') return null;

  const match = /^([+-])?(\d+)(?:\.(\d*))?$/.exec(value.trim());
  if (!match) return null;

  const [, sign, whole, fraction = ''] = match;

  const centavos = BigInt(whole) * 100n + BigInt((fraction + '00').slice(0, 2));

  // The third fraction digit decides the second. Half-away-from-zero, applied
  // to the magnitude, so -0.005 and 0.005 both move away from zero.
  const rounded = Number(fraction[2] ?? '0') >= 5 ? centavos + 1n : centavos;

  // Beyond this a JavaScript number silently loses whole centavos. An amount
  // that large is a bug upstream, and reporting nothing beats reporting a
  // figure that is quietly wrong.
  if (rounded > BigInt(Number.MAX_SAFE_INTEGER)) return null;

  return { minorUnits: (sign === '-' ? -1 : 1) * Number(rounded), currency: 'PHP' };
}

/**
 * The same, for a field the caller knows the server always sends.
 *
 * Absent input becomes zero here, which is only correct where zero is the
 * server's own meaning for "no amount" — a payslip line that does not apply,
 * not a total that failed to arrive.
 */
export function moneyFromDecimalStringOrZero(value: string | null | undefined): Money {
  return moneyFromDecimalString(value) ?? ZERO_PHP;
}

export function addMoney(...values: Money[]): Money {
  return {
    minorUnits: values.reduce((total, value) => total + value.minorUnits, 0),
    currency: 'PHP',
  };
}

export function subtractMoney(a: Money, b: Money): Money {
  return { minorUnits: a.minorUnits - b.minorUnits, currency: 'PHP' };
}

export function sumMoney(values: Money[]): Money {
  return addMoney(...values);
}

export function isZeroMoney(value: Money): boolean {
  return value.minorUnits === 0;
}

export function isNegativeMoney(value: Money): boolean {
  return value.minorUnits < 0;
}

const PESO_SIGN = '₱';

function groupDigits(value: string): string {
  return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** `₱24,500.00` — the standard payslip and summary format. */
export function formatMoney(value: Money | null | undefined, options?: { sign?: boolean }): string {
  if (!value) return '—';
  const negative = value.minorUnits < 0;
  const absolute = Math.abs(value.minorUnits);
  const whole = Math.floor(absolute / 100);
  const cents = absolute % 100;
  const formatted = `${PESO_SIGN}${groupDigits(String(whole))}.${String(cents).padStart(2, '0')}`;

  if (negative) return `-${formatted}`;
  if (options?.sign) return `+${formatted}`;
  return formatted;
}

/** `24,500.00 pesos` — screen-reader friendly. */
export function formatMoneyAccessible(value: Money | null | undefined): string {
  if (!value) return 'not available';
  const pesos = Math.abs(value.minorUnits) / 100;
  const sign = value.minorUnits < 0 ? 'minus ' : '';
  return `${sign}${pesos.toFixed(2)} pesos`;
}

/** `₱24.5k` — compact form for tight metric tiles. */
export function formatMoneyCompact(value: Money | null | undefined): string {
  if (!value) return '—';
  const pesos = value.minorUnits / 100;
  if (Math.abs(pesos) >= 1_000_000) {
    return `${PESO_SIGN}${(pesos / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(pesos) >= 1_000) {
    return `${PESO_SIGN}${(pesos / 1_000).toFixed(1)}k`;
  }
  return formatMoney(value);
}
